/**
 * Utility functions for activity feed calculations
 */
import { isEffectivelyZero } from '@splitfiesta/shared';

export interface ActivityPayment {
  userId: string;
  amount: number;
}

export interface ActivitySplit {
  userId: string;
  amount: number;
}

export type UserPosition = {
  type: 'owe' | 'lent' | 'none';
  amount: number;
};

/**
 * Calculate a user's financial position in an expense activity
 */
export function calculateUserPosition(
  currentUserId: string | undefined,
  paidBy: ActivityPayment[] | undefined,
  splits: ActivitySplit[] | undefined,
  userShare?: number,
  currency: string = 'USD'
): UserPosition {
  if (!currentUserId) {
    return { type: 'none', amount: 0 };
  }

  // Backward compatibility with userShare
  if (!paidBy || !splits) {
    if (userShare && userShare > 0) {
      return { type: 'owe', amount: userShare };
    }
    return { type: 'none', amount: 0 };
  }

  // Calculate what the user paid
  const userPaid = paidBy
    .filter(p => p.userId === currentUserId)
    .reduce((sum, p) => sum + p.amount, 0);

  // Calculate what the user's share is
  const userSplit = splits
    .filter(s => s.userId === currentUserId)
    .reduce((sum, s) => sum + s.amount, 0);

  // Determine the user's position
  const difference = userPaid - userSplit;

  if (isEffectivelyZero(difference, currency)) {
    return { type: 'none', amount: 0 };
  } else if (difference > 0) {
    return { type: 'lent', amount: difference };
  } else {
    return { type: 'owe', amount: Math.abs(difference) };
  }
}
